import React, { FC, useEffect, useState } from "react";
import { Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useQuery, useSubscription } from "@apollo/client";
import { GET_NOTIFICATIONS } from "../../graphql/queries";
import {
    BuildNotification,
    User,
    UserNotification,
    UserNotificationData,
    UserNotificationQueryData,
    UserNotificationVariables
} from "../../models/types";
import { NOTIFICATION_SUBSCRIPTION } from "../../graphql/subscriptions";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NotificationsModal } from "../modals/NotificationsModal";
import { useLocalState } from "../../graphql/hooks";
import cx from "classnames";
import styles from "./NotificationsIcon.module.scss";

interface Params {
    user: User | undefined;
    mobileMode: boolean;
}

export const NotificationsIcon: FC<Params> = ({ user, mobileMode }) => {
    const state = useLocalState();
    const [showModificationModal, updateShowModificationModal] =
        useState(false);
    const { data } = useQuery<UserNotificationQueryData>(GET_NOTIFICATIONS, {
        skip: user === undefined
    });

    useSubscription<UserNotificationData, UserNotificationVariables>(
        NOTIFICATION_SUBSCRIPTION,
        {
            variables: { userId: user?.id },
            skip: user === undefined,
            onData: ({ data: { data: { userNotification } = {} }, client }) => {
                const currentNotificationsData =
                    client.cache.readQuery<UserNotificationQueryData>({
                        query: GET_NOTIFICATIONS
                    });
                const currentNotifications =
                    currentNotificationsData?.getNotifications;
                if (currentNotifications && userNotification) {
                    const updatedNotifications = [
                        ...currentNotifications,
                        userNotification
                    ];
                    client.cache.writeQuery<UserNotificationQueryData>({
                        query: GET_NOTIFICATIONS,
                        data: { getNotifications: updatedNotifications }
                    });
                }
            }
        }
    );

    if (!user) {
        return null;
    }

    const [shaking, setShaking] = useState(false);

    const newBuildNotifications = state.buildNotifications.some(
        (item: BuildNotification) => item.isNew
    );

    const pendingNotifications = data?.getNotifications.some(
        (item: UserNotification) => item.isNew
    );

    const hasNew = newBuildNotifications || pendingNotifications;

    const unreadCount =
        state.buildNotifications.length +
        (data?.getNotifications.filter(n => n.isNew).length ?? 0);

    useEffect(() => {
        if (hasNew) {
            setShaking(true);
            const timer = setTimeout(() => setShaking(false), 500);
            return () => clearTimeout(timer);
        }
    }, [state.buildNotifications.length, data?.getNotifications.length]);

    return (
        <>
            <div className={cx({ "ms-0 ps-0 me-1": mobileMode })}>
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 550, hide: 300 }}
                    overlay={
                        <Tooltip id="notifications-tooltip">
                            {unreadCount > 0
                                ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
                                : "Notifications"}
                        </Tooltip>
                    }
                >
                    <Nav.Link
                        onClick={() => updateShowModificationModal(true)}
                        className="ms-2"
                    >
                        <div className={styles.wrapper}>
                            <FontAwesomeIcon
                                icon={faBell}
                                size="lg"
                                className={cx({ [styles.shake]: shaking })}
                            />
                            {unreadCount > 0 && (
                                <span className={styles.badge}>
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </div>
                    </Nav.Link>
                </OverlayTrigger>
            </div>
            {showModificationModal ? (
                <NotificationsModal
                    show={showModificationModal}
                    notifications={data?.getNotifications}
                    buildNotifications={state.buildNotifications}
                    onClose={() => updateShowModificationModal(false)}
                />
            ) : null}
        </>
    );
};
